/**
 * FIXME: This logic will be later moved to Backend. This file is used to map the UI fields to PDF form fields
 * (there are redundant ui fields still passed in the payload (as-is) which needs cleanup later)
 */

export type FieldMapping =
  | { type: 'direct'; pdfKey: string }
  | { type: 'lookup'; pdfKey: string }
  | { type: 'dateSplit'; pdfKeys: [string, string, string] }
  | { type: 'address'; pdfKey: string; line2UiKey: string }
  | { type: 'nameOrSingle'; pdfKey: string; flagUiKey: string; textUiKey: string }
  | { type: 'yesNo'; yesKey: string; noKey: string }
  | { type: 'negate'; pdfKey: string }
  | { type: 'lookupWithFallback'; pdfKey: string; otherUiKey: string }
  | { type: 'dateReformat'; pdfKey: string };

type DerivedField =
  | { type: 'concat'; pdfKey: string; sourceKeys: string[]; separator?: string }
  | { type: 'copy'; pdfKey: string; sourceKey: string }
  | { type: 'today'; pdfKey: string }
  | { type: 'todaySplit'; pdfKeys: [string, string, string] }
  | { type: 'initials'; pdfKey: string; sourceKeys: string[] }
  | { type: 'nameConcat'; pdfKey: string; sourceKeys: string[]; singleNameFlag: string; singleNameKey: string }
  | { type: 'null'; pdfKey: string }
  | { type: 'conditionalCopy'; pdfKey: string; flagKey: string; trueSourceKey: string; falseSourceKey: string };

const direct = (pdfKey: string): FieldMapping => ({ type: 'direct', pdfKey });
const lookup = (pdfKey: string): FieldMapping => ({ type: 'lookup', pdfKey });
const dateSplit = (pdfKeys: [string, string, string]): FieldMapping => ({ type: 'dateSplit', pdfKeys });
const address = (pdfKey: string, line2UiKey: string): FieldMapping => ({ type: 'address', pdfKey, line2UiKey });
const nameOrSingle = (pdfKey: string, flagUiKey: string, textUiKey: string): FieldMapping =>
  ({ type: 'nameOrSingle', pdfKey, flagUiKey, textUiKey });
const yesNo = (yesKey: string, noKey: string): FieldMapping => ({ type: 'yesNo', yesKey, noKey });
const negate = (pdfKey: string): FieldMapping => ({ type: 'negate', pdfKey });
const lookupWithFallback = (pdfKey: string, otherUiKey: string): FieldMapping =>
  ({ type: 'lookupWithFallback', pdfKey, otherUiKey });
const dateReformat = (pdfKey: string): FieldMapping => ({ type: 'dateReformat', pdfKey });

export type FieldMappingEntry = FieldMapping | FieldMapping[];

export const VECON_FIELD_MAPPINGS: Record<string, FieldMappingEntry> = {
  hasLived12YearsInOntario: direct('hasLived12YearsInOntario'),
  numberOFYears: direct('livedYears'),
  numberOfMonths: direct('livedMonths'),
  hasLivedInOntarioConfirmation: direct('hasLivedInOntarioConfirmation'),

  currentFirstName: direct('currentFirstName'),
  currentMiddleName: direct('currentMiddleName'),
  hasNoMiddleName: negate('hasMiddleName'),
  currentLastName: nameOrSingle('currentLastOrSingleName', 'hasOnlySingleName', 'currentSingleName'),
  hasOnlySingleName: direct('hasOnlySingleName'),
  currentSingleName: direct('currentSingleName'),

  dateOfBirth: [dateSplit(['dobYear', 'dobMonth', 'dobDay']), direct('consentNewDOB')],

  countryBorn: lookupWithFallback('bornCountry', 'otherCountryBorn'),
  provinceBorn: lookupWithFallback('bornProvince', 'otherProvinceBorn'),
  cityBorn: direct('bornCity'),

  displaySexOnBirthCertificate: [direct('displaySexOnBirthCertificate'), negate('displaySexOnBC')],

  proposedFirstName: [direct('proposedFirstName'), direct('consentNewFirstName')],
  proposedMiddleName: [direct('proposedMiddleName'), direct('consentNewMiddleName')],
  hasNoProposedMiddleName: direct('hasNoProposedMiddleName'),
  proposedLastName: [
    nameOrSingle('proposedLastOrSingleName', 'hasProposedSingleName', 'proposedNewLegalSingleName'),
    nameOrSingle('consentNewLastOrSingleName', 'hasProposedSingleName', 'proposedNewLegalSingleName'),
  ],
  hasProposedSingleName: direct('hasProposedSingleName'),
  proposedNewLegalSingleName: direct('proposedSingleName'),

  isNameChangeForCulturalReason: direct('isNameChangeForCulturalReason'),
  isIndigenous: direct('isIndigenous'),
  isTraditionalCulturalMember: direct('isTraditionalCulturalMember'),
  traditionalCultueName: direct('traditionalCultueName'),

  isMarried: [direct('isMarried'), direct('noticeMarriedChkBox'), yesNo('marriedYes', 'marriedNo')],
  spouseFirstName: direct('spouseFirstName'),
  spouseMiddleName: direct('spouseMiddleName'),
  hasSpouseNoMiddleName: direct('hasSpouseNoMiddleName'),
  spouseLastName: nameOrSingle('spouseLastOrSingleName', 'hasSpouseHasOnlySingleName', 'spouseSingleName'),
  hasSpouseHasOnlySingleName: direct('hasSpouseHasOnlySingleName'),
  spouseSingleName: direct('spouseSingleName'),
  marriageDate: dateSplit(['marriageDtYear', 'marriageDtMonth', 'marriageDtDay']),
  countryMarried: lookupWithFallback('marriedCountry', 'otherCountryMarried'),
  provinceMarried: lookupWithFallback('marriedProvince', 'otherProvinceMarried'),
  cityMarried: direct('marriedCity'),

  hasSubmittedConjugalRelationshipForm: [direct('hasSubmittedConjugalRelationshipForm'), yesNo('jointDecYes', 'jointDecNo')],
  isConjugalRelationshipValid: [direct('isConjugalRelationshipValid'), direct('noticePartnerChkBox'), yesNo('decConjRelYes', 'decConjRelNo')],
  partnerFirstName: direct('partnerFirstName'),
  partnerMiddleName: direct('partnerMiddleName'),
  hasPartnerNoMiddleName: direct('hasPartnerNoMiddleName'),
  partnerLastName: nameOrSingle('partnerLastOrSingleName', 'partnerHasOnlySingleName', 'partnerSingleName'),
  partnerHasOnlySingleName: direct('partnerHasOnlySingleName'),
  partnerSingleName: direct('partnerSingleName'),
  jointDeclarationDate: dateSplit(['jointDecDtYear', 'jointDecDtMonth', 'jointDecDtDay']),

  numberOfParentsOnBirthRegistration: direct('numberOfParentsOnBirthRegistration'),

  parent1CurrentFirstName: direct('parent1FirstName'),
  parent1CurrentMiddleName: direct('parent1MiddleName'),
  parent1HasNoMiddleName: direct('parent1HasNoMiddleName'),
  parent1CurrentLastName: nameOrSingle('parent1LastorSingleName', 'parent1HasSingleName', 'parent1SingleName'),
  parent1HasSingleName: direct('parent1HasSingleName'),
  parent1SingleName: direct('parent1SingleName'),
  parent1NameDifferentAtBirth: direct('parent1NameDifferentAtBirth'),
  parent1LegalNameDifferentAtBirth: direct('parent1NameAtBirth'),
  parent1AnyOtherNames: direct('parent1AnyOtherNames'),
  parent1PreviousLegalLastNames: direct('parent1PreviousNames'),

  parent2CurrentFirstName: direct('parent2FirstName'),
  parent2CurrentMiddleName: direct('parent2MiddleName'),
  parent2HasNoMiddleName: direct('parent2HasNoMiddleName'),
  parent2CurrentLastName: nameOrSingle('parent2LastorSingleName', 'parent2HasSingleName', 'parent2SingleName'),
  parent2HasSingleName: direct('parent2HasSingleName'),
  parent2SingleName: direct('parent2SingleName'),
  parent2NameDifferentAtBirth: direct('parent2NameDifferentAtBirth'),
  parent2LegalNameDifferentAtBirth: direct('parent2NameAtBirth'),
  parent2AnyOtherNames: direct('parent2AnyOtherNames'),
  parent2PreviousLegalLastNames: direct('parent2PreviousNames'),

  parent3CurrentFirstName: direct('parent3FirstName'),
  parent3CurrentMiddleName: direct('parent3MiddleName'),
  parent3HasNoMiddleName: direct('parent3HasNoMiddleName'),
  parent3CurrentLastName: nameOrSingle('parent3LastorSingleName', 'parent3HasSingleName', 'parent3SingleName'),
  parent3HasSingleName: direct('parent3HasSingleName'),
  parent3SingleName: direct('parent3SingleName'),
  parent3NameDifferentAtBirth: direct('parent3NameDifferentAtBirth'),
  parent3LegalNameDifferentAtBirth: direct('parent3NameAtBirth'),
  parent3AnyOtherNames: direct('parent3AnyOtherNames'),
  parent3PreviousLegalLastNames: direct('parent3PreviousNames'),

  parent4CurrentFirstName: direct('parent4FirstName'),
  parent4CurrentMiddleName: direct('parent4MiddleName'),
  parent4HasNoMiddleName: direct('parent4HasNoMiddleName'),
  parent4CurrentLastName: nameOrSingle('parent4LastorSingleName', 'parent4HasSingleName', 'parent4SingleName'),
  parent4HasSingleName: direct('parent4HasSingleName'),
  parent4SingleName: direct('parent4SingleName'),
  parent4NameDifferentAtBirth: direct('parent4NameDifferentAtBirth'),
  parent4LegalNameDifferentAtBirth: direct('parent4NameAtBirth'),
  parent4AnyOtherNames: direct('parent4AnyOtherNames'),
  parent4PreviousLegalLastNames: direct('parent4PreviousNames'),

  whyChangeLegalName: direct('whyChangeYourName'),

  hasLegallyChangedNameBefore: [direct('hasLegallyChangedNameBefore'), yesNo('everChangedYes', 'everChangedNo')],
  wasPastRequestRefused: [direct('wasPastRequestRefused'), yesNo('refusedYes', 'refusedNo')],
  numberOfPreviousNameChanges: direct('numberOfPreviousNameChanges'),

  firstNamePriorToChange1: direct('beforeChangedFirstName'),
  middleNamePriorToChange1: direct('beforeChangedMiddleName'),
  noMiddleNamePrior1: direct('noMiddleNamePrior1'),
  lastNamePriorToChange1: nameOrSingle('beforeChangedLastOrSingleName', 'hadSingleNamePrior1', 'singleNamePrior1'),
  hadSingleNamePrior1: direct('hadSingleNamePrior1'),
  singleNamePrior1: direct('singleNamePrior1'),
  firstNameAfterChange1: direct('changedFirstName'),
  middleNameAfterChange1: direct('changedMiddleName'),
  noMiddleNameAfter1: direct('noMiddleNameAfter1'),
  lastNameAfterChange1: nameOrSingle('changedLastOrSingleName', 'hadSingleNameAfter1', 'singleNameAfter1'),
  hadSingleNameAfter1: direct('hadSingleNameAfter1'),
  singleNameAfter1: direct('singleNameAfter1'),
  dateOfNameChange1: dateSplit(['everChangedYear', 'everChangedMonth', 'everChangedDay']),
  countryNameChange1: lookupWithFallback('changedCountry', 'otherCountryNameChange1'),
  provinceNameChange1: lookupWithFallback('changedProvince', 'otherProvinceNameChange1'),

  firstNamePriorToChange2: direct('beforeChangedFirstName2'),
  middleNamePriorToChange2: direct('beforeChangedMiddleName2'),
  noMiddleNamePrior2: direct('noMiddleNamePrior2'),
  lastNamePriorToChange2: nameOrSingle('beforeChangedLastOrSingleName2', 'hadSingleNamePrior2', 'singleNamePrior2'),
  hadSingleNamePrior2: direct('hadSingleNamePrior2'),
  singleNamePrior2: direct('singleNamePrior2'),
  firstNameAfterChange2: direct('changedFirstName2'),
  middleNameAfterChange2: direct('changedMiddleName2'),
  noMiddleNameAfter2: direct('noMiddleNameAfter2'),
  lastNameAfterChange2: nameOrSingle('changedLastOrSingleName2', 'hadSingleNameAfter2', 'singleNameAfter2'),
  hadSingleNameAfter2: direct('hadSingleNameAfter2'),
  singleNameAfter2: direct('singleNameAfter2'),
  dateOfNameChange2: dateSplit(['everChangedYear2', 'everChangedMonth2', 'everChangedDay2']),
  countryNameChange2: lookupWithFallback('changedCountry2', 'otherCountryNameChange2'),
  provinceNameChange2: lookupWithFallback('changedProvince2', 'otherProvinceNameChange2'),

  firstNamePriorToChange3: direct('beforeChangedFirstName3'),
  middleNamePriorToChange3: direct('beforeChangedMiddleName3'),
  noMiddleNamePrior3: direct('noMiddleNamePrior3'),
  lastNamePriorToChange3: nameOrSingle('beforeChangedLastOrSingleName3', 'hadSingleNamePrior3', 'singleNamePrior3'),
  hadSingleNamePrior3: direct('hadSingleNamePrior3'),
  singleNamePrior3: direct('singleNamePrior3'),
  firstNameAfterChange3: direct('changedFirstName3'),
  middleNameAfterChange3: direct('changedMiddleName3'),
  noMiddleNameAfter3: direct('noMiddleNameAfter3'),
  lastNameAfterChange3: nameOrSingle('changedLastOrSingleName3', 'hadSingleNameAfter3', 'singleNameAfter3'),
  hadSingleNameAfter3: direct('hadSingleNameAfter3'),
  singleNameAfter3: direct('singleNameAfter3'),
  dateOfNameChange3: dateSplit(['everChangedYear3', 'everChangedMonth3', 'everChangedDay3']),
  countryNameChange3: lookupWithFallback('changedCountry3', 'otherCountryNameChange3'),
  provinceNameChange3: lookupWithFallback('changedProvince3', 'otherProvinceNameChange3'),

  firstNamePriorToChange4: direct('beforeChangedFirstName4'),
  middleNamePriorToChange4: direct('beforeChangedMiddleName4'),
  noMiddleNamePrior4: direct('noMiddleNamePrior4'),
  lastNamePriorToChange4: nameOrSingle('beforeChangedLastOrSingleName4', 'hadSingleNamePrior4', 'singleNamePrior4'),
  hadSingleNamePrior4: direct('hadSingleNamePrior4'),
  singleNamePrior4: direct('singleNamePrior4'),
  firstNameAfterChange4: direct('changedFirstName4'),
  middleNameAfterChange4: direct('changedMiddleName4'),
  noMiddleNameAfter4: direct('noMiddleNameAfter4'),
  lastNameAfterChange4: nameOrSingle('changedLastOrSingleName4', 'hadSingleNameAfter4', 'singleNameAfter4'),
  hadSingleNameAfter4: direct('hadSingleNameAfter4'),
  singleNameAfter4: direct('singleNameAfter4'),
  dateOfNameChange4: dateSplit(['everChangedYear4', 'everChangedMonth4', 'everChangedDay4']),
  countryNameChange4: lookupWithFallback('changedCountry4', 'otherCountryNameChange4'),
  provinceNameChange4: lookupWithFallback('changedProvince4', 'otherProvinceNameChange4'),

  hasNonCriminalProceedings: [direct('hasNonCriminalProceedings'), yesNo('q1Yes', 'q1No')],
  courtNameQ1: direct('q1CourtName'),
  courtFileNumberQ1: direct('q1CourtFileNumber'),
  provinceQ1: lookup('provinceQ1'),
  cityQ1: direct('cityQ1'),
  addressLine1Q1: address('q1CourtAddress', 'addressLine2Q1'),
  addressLine2Q1: direct('addressLine2Q1'),
  postalCodeQ1: direct('postalCodeQ1'),
  describeProceedingsQ1: direct('q1Proceedings'),

  hasOutstandingLawEnforcementOrders: [direct('hasOutstandingLawEnforcementOrders'), yesNo('q2Yes', 'q2No')],
  courtNameQ2: direct('q2CourtName'),
  courtFileNumberQ2: direct('q2CourtFileNumber'),
  provinceQ2: lookup('provinceQ2'),
  cityQ2: direct('cityQ2'),
  addressLine1Q2: address('q2CourtAddress', 'addressLine2Q2'),
  addressLine2Q2: direct('addressLine2Q2'),
  postalCodeQ2: direct('postalCodeQ2'),
  describeProceedingsQ2: direct('q2Proceedings'),

  hasBeenConvicted: [direct('hasBeenConvicted'), yesNo('q3Yes', 'q3No')],
  detailOffencesQ3: direct('q3Offences'),
  hasBeenFoundGuiltyNoDischarge: [direct('hasBeenFoundGuiltyNoDischarge'), yesNo('q4Yes', 'q4No')],
  detailOffencesQ4: direct('q4Offences'),

  hasPendingCriminalCharges: [direct('hasPendingCriminalCharges'), yesNo('q5Yes', 'q5No')],
  courtNameQ5: direct('q5CourtName'),
  courtFileNumberQ5: direct('q5CourtFileNumber'),
  countryQ5: lookupWithFallback('countryQ5', 'otherCountryQ5'),
  provinceQ5: lookupWithFallback('provinceQ5', 'otherProvinceQ5'),
  cityQ5: direct('cityQ5'),
  addressLine1Q5: address('q5CourtAddress', 'addressLine2Q5'),
  addressLine2Q5: direct('addressLine2Q5'),
  postalCodeQ5: direct('postalCodeQ5'),
  describeChargesQ5: direct('q5Charges'),

  hasCourtOrderedPayment: [direct('hasCourtOrderedPayment'), yesNo('tribunalYes', 'tribunalNo')],
  courtTribunalFileNumber: direct('tribunalFileNumber'),
  courtTribunalName: direct('tribunalName'),
  dateCourtTribunalOrder: dateReformat('tribunalDt'),
  suedByFirstName: direct('suedByName'),
  suedByMiddleName: direct('suedByMiddleName'),
  suedByNoMiddleName: direct('suedByNoMiddleName'),
  suedByLastName: direct('suedByLastName'),
  suedBySingleName: direct('suedBySingleName'),
  suedBySingleNameText: direct('suedBySingleNameText'),
  provinceCourtTribunal: lookup('provinceCourtTribunal'),
  cityCourtTribunal: direct('cityCourtTribunal'),
  addressLine1CourtTribunal: address('tribunalAddress', 'addressLine2CourtTribunal'),
  addressLine2CourtTribunal: direct('addressLine2CourtTribunal'),
  postalCodeCourtTribunal: direct('postalCodeCourtTribunal'),

  hasCourtOrderSeizeProperty: [direct('hasCourtOrderSeizeProperty'), yesNo('sheriffYes', 'sheriffNo')],
  writNumber: direct('writNumber'),
  sheriffFirstName: direct('sheriffName'),
  sheriffMiddleName: direct('sheriffMiddleName'),
  sheriffNoMiddleName: direct('sheriffNoMiddleName'),
  sheriffLastName: direct('sheriffLastName'),
  sheriffSingleName: direct('sheriffSingleName'),
  sheriffSingleNameText: direct('sheriffSingleNameText'),
  provinceSheriff: lookup('provinceSheriff'),
  citySheriff: direct('citySheriff'),
  addressLine1Sheriff: address('sheriffAddress', 'addressLine2Sheriff'),
  addressLine2Sheriff: direct('addressLine2Sheriff'),
  postalCodeSheriff: direct('postalCodeSheriff'),

  hasLiensOrSecurityInterests: [direct('hasLiensOrSecurityInterests'), yesNo('liensYes', 'liensNo')],
  registrationNumberLien: direct('lienRegNumber'),
  lienHolderFirstName: direct('lienHolderName'),
  lienHolderMiddleName: direct('lienHolderMiddleName'),
  lienHolderNoMiddleName: direct('lienHolderNoMiddleName'),
  lienHolderLastName: direct('lienHolderLastName'),
  lienHolderSingleName: direct('lienHolderSingleName'),
  lienHolderSingleNameText: direct('lienHolderSingleNameText'),
  amountMoneyOwed: direct('lienAmount'),

  hasFinancingStatements: [direct('hasFinancingStatements'), yesNo('ppsaYes', 'ppsaNo')],
  registrationNumberPPSA: direct('ppsaRegNumber'),

  isUndischargedBankrupt: [direct('isUndischargedBankrupt'), yesNo('bankruptcyYes', 'bankruptcyNo')],
  describeBankruptcy: direct('describeBankruptcy'),

  noticeSpouseFirstName: direct('_noticeAckFirstName'),
  noticeSpouseMiddleName: direct('_noticeAckMiddleName'),
  noticeSpouseLastName: nameOrSingle('_noticeAckLastName', 'noticeSpouseSingleName', 'noticeSpouseSingleNameText'),
  noticeSpouseNoMiddleName: direct('noticeSpouseNoMiddleName'),
  noticeSpouseSingleName: direct('noticeSpouseSingleName'),
  noticeSpouseSingleNameText: direct('noticeSpouseSingleNameText'),
  noticeSpouseSameAddress: direct('noticeSpouseSameAddress'),
  noticeSpouseCountry: lookupWithFallback('noticeSpouseCountry', 'otherNoticeSpouseCountry'),
  noticeSpouseProvince: lookupWithFallback('noticeSpouseProvince', 'otherNoticeSpouseProvince'),
  noticeSpouseCity: direct('noticeSpouseCity'),
  noticeSpouseAddressLine1: address('_noticeSpouseAddress', 'noticeSpouseAddressLine2'),
  noticeSpouseAddressLine2: direct('noticeSpouseAddressLine2'),
  noticeSpousePostalCode: direct('noticeSpousePostalCode'),

  numberOfCustodyPersons: direct('numberOfCustodyPersons'),

  consentPerson1FirstName: direct('consentPerson1FirstName'),
  consentPerson1MiddleName: direct('consentPerson1MiddleName'),
  consentPerson1NoMiddleName: direct('consentPerson1NoMiddleName'),
  consentPerson1LastName: nameOrSingle('consentPerson1LastName', 'consentPerson1SingleName', 'consentPerson1SingleNameText'),
  consentPerson1SingleName: direct('consentPerson1SingleName'),
  consentPerson1SingleNameText: direct('consentPerson1SingleNameText'),
  consentPerson1SameAddress: direct('consentPerson1SameAddress'),
  consentPerson1Country: lookupWithFallback('consentPerson1Country', 'otherConsentPerson1Country'),
  consentPerson1Province: lookupWithFallback('consentPerson1Province', 'otherConsentPerson1Province'),
  consentPerson1City: direct('consentPerson1City'),
  consentPerson1AddressLine1: address('consentMailingAddressLine1', 'consentPerson1AddressLine2'),
  consentPerson1PostalCode: direct('consentPerson1PostalCode'),

  consentPerson2FirstName: direct('consentPerson2FirstName'),
  consentPerson2MiddleName: direct('consentPerson2MiddleName'),
  consentPerson2NoMiddleName: direct('consentPerson2NoMiddleName'),
  consentPerson2LastName: nameOrSingle('consentPerson2LastName', 'consentPerson2SingleName', 'consentPerson2SingleNameText'),
  consentPerson2SingleName: direct('consentPerson2SingleName'),
  consentPerson2SingleNameText: direct('consentPerson2SingleNameText'),
  consentPerson2SameAddress: direct('consentPerson2SameAddress'),
  consentPerson2Country: lookupWithFallback('consentPerson2Country', 'otherConsentPerson2Country'),
  consentPerson2Province: lookupWithFallback('consentPerson2Province', 'otherConsentPerson2Province'),
  consentPerson2City: direct('consentPerson2City'),
  consentPerson2AddressLine1: address('consentPerson2AddressLine1', 'consentPerson2AddressLine2'),
  consentPerson2PostalCode: direct('consentPerson2PostalCode'),

  consentPerson3FirstName: direct('consentPerson3FirstName'),
  consentPerson3MiddleName: direct('consentPerson3MiddleName'),
  consentPerson3NoMiddleName: direct('consentPerson3NoMiddleName'),
  consentPerson3LastName: nameOrSingle('consentPerson3LastName', 'consentPerson3SingleName', 'consentPerson3SingleNameText'),
  consentPerson3SingleName: direct('consentPerson3SingleName'),
  consentPerson3SingleNameText: direct('consentPerson3SingleNameText'),
  consentPerson3SameAddress: direct('consentPerson3SameAddress'),
  consentPerson3Country: lookupWithFallback('consentPerson3Country', 'otherConsentPerson3Country'),
  consentPerson3Province: lookupWithFallback('consentPerson3Province', 'otherConsentPerson3Province'),
  consentPerson3City: direct('consentPerson3City'),
  consentPerson3AddressLine1: address('consentPerson3AddressLine1', 'consentPerson3AddressLine2'),
  consentPerson3PostalCode: direct('consentPerson3PostalCode'),

  consentPerson4FirstName: direct('consentPerson4FirstName'),
  consentPerson4MiddleName: direct('consentPerson4MiddleName'),
  consentPerson4NoMiddleName: direct('consentPerson4NoMiddleName'),
  consentPerson4LastName: nameOrSingle('consentPerson4LastName', 'consentPerson4SingleName', 'consentPerson4SingleNameText'),
  consentPerson4SingleName: direct('consentPerson4SingleName'),
  consentPerson4SingleNameText: direct('consentPerson4SingleNameText'),
  consentPerson4SameAddress: direct('consentPerson4SameAddress'),
  consentPerson4Country: lookupWithFallback('consentPerson4Country', 'otherConsentPerson4Country'),
  consentPerson4Province: lookupWithFallback('consentPerson4Province', 'otherConsentPerson4Province'),
  consentPerson4City: direct('consentPerson4City'),
  consentPerson4AddressLine1: address('consentPerson4AddressLine1', 'consentPerson4AddressLine2'),
  consentPerson4PostalCode: direct('consentPerson4PostalCode'),

  consentPerson5FirstName: direct('consentPerson5FirstName'),
  consentPerson5MiddleName: direct('consentPerson5MiddleName'),
  consentPerson5NoMiddleName: direct('consentPerson5NoMiddleName'),
  consentPerson5LastName: nameOrSingle('consentPerson5LastName', 'consentPerson5SingleName', 'consentPerson5SingleNameText'),
  consentPerson5SingleName: direct('consentPerson5SingleName'),
  consentPerson5SingleNameText: direct('consentPerson5SingleNameText'),
  consentPerson5SameAddress: direct('consentPerson5SameAddress'),
  consentPerson5Country: lookupWithFallback('consentPerson5Country', 'otherConsentPerson5Country'),
  consentPerson5Province: lookupWithFallback('consentPerson5Province', 'otherConsentPerson5Province'),
  consentPerson5City: direct('consentPerson5City'),
  consentPerson5AddressLine1: address('consentPerson5AddressLine1', 'consentPerson5AddressLine2'),
  consentPerson5PostalCode: direct('consentPerson5PostalCode'),

  mailingCountry: lookupWithFallback('mailingCountry', 'otherMailingCountry'),
  mailingProvince: lookupWithFallback('mailingProvince', 'otherMailingProvince'),
  mailingCity: direct('mailingCity'),
  mailingAddressLine1: direct('mailingStreetNumber'),
  mailingAddressLine2: direct('mailingAptUnit'),
  mailingRouteNumber: direct('mailingPOBox'),
  mailingBuzzerNumber: direct('mailingBuzzerNo'),
  mailingPostalCode: direct('mailingPostalCode'),
  phoneNumber: [direct('phoneDaytime'), direct('phoneAlternateDaytime')],
  emailAddress: direct('mailingEmail'),
  saveCopyConsent: direct('saveCopyConsent'),
};

const DERIVED_PDF_FIELDS: DerivedField[] = [
  { type: 'concat', pdfKey: 'q1CourtAddress', sourceKeys: ['q1CourtAddress', 'addressLine2Q1', 'cityQ1', 'provinceQ1', 'postalCodeQ1'], separator: ', ' },
  { type: 'concat', pdfKey: 'q2CourtAddress', sourceKeys: ['q2CourtAddress', 'addressLine2Q2', 'cityQ2', 'provinceQ2', 'postalCodeQ2'], separator: ', ' },
  { type: 'concat', pdfKey: 'q5CourtAddress', sourceKeys: ['q5CourtAddress', 'addressLine2Q5', 'countryQ5', 'cityQ5', 'provinceQ5', 'postalCodeQ5'], separator: ', ' },
  { type: 'concat', pdfKey: 'tribunalAddress', sourceKeys: ['tribunalAddress', 'addressLine2CourtTribunal', 'cityCourtTribunal', 'provinceCourtTribunal', 'postalCodeCourtTribunal'], separator: ', ' },
  { type: 'concat', pdfKey: 'sheriffAddress', sourceKeys: ['sheriffAddress', 'addressLine2Sheriff', 'citySheriff', 'provinceSheriff', 'postalCodeSheriff'], separator: ', ' },
  { type: 'concat', pdfKey: '_noticeSpouseFullAddress', sourceKeys: ['_noticeSpouseAddress', 'noticeSpouseCity', 'noticeSpouseCountry', 'noticeSpouseProvince', 'noticeSpousePostalCode'], separator: ', ' },

  { type: 'nameConcat', pdfKey: 'suedByName', sourceKeys: ['suedByName', 'suedByMiddleName', 'suedByLastName'], singleNameFlag: 'suedBySingleName', singleNameKey: 'suedBySingleNameText' },
  { type: 'nameConcat', pdfKey: 'sheriffName', sourceKeys: ['sheriffName', 'sheriffMiddleName', 'sheriffLastName'], singleNameFlag: 'sheriffSingleName', singleNameKey: 'sheriffSingleNameText' },
  { type: 'nameConcat', pdfKey: 'lienHolderName', sourceKeys: ['lienHolderName', 'lienHolderMiddleName', 'lienHolderLastName'], singleNameFlag: 'lienHolderSingleName', singleNameKey: 'lienHolderSingleNameText' },

  { type: 'nameConcat', pdfKey: 'parent1FirstName', sourceKeys: ['parent1FirstName', 'parent1MiddleName', 'parent1LastorSingleName'], singleNameFlag: 'parent1HasSingleName', singleNameKey: 'parent1SingleName' },
  { type: 'nameConcat', pdfKey: 'parent2FirstName', sourceKeys: ['parent2FirstName', 'parent2MiddleName', 'parent2LastorSingleName'], singleNameFlag: 'parent2HasSingleName', singleNameKey: 'parent2SingleName' },
  { type: 'nameConcat', pdfKey: 'parent3FirstName', sourceKeys: ['parent3FirstName', 'parent3MiddleName', 'parent3LastorSingleName'], singleNameFlag: 'parent3HasSingleName', singleNameKey: 'parent3SingleName' },
  { type: 'nameConcat', pdfKey: 'parent4FirstName', sourceKeys: ['parent4FirstName', 'parent4MiddleName', 'parent4LastorSingleName'], singleNameFlag: 'parent4HasSingleName', singleNameKey: 'parent4SingleName' },

  { type: 'concat', pdfKey: 'applicantMailingAddress', sourceKeys: ['mailingPOBox', 'mailingAptUnit', 'mailingBuzzerNo', 'mailingStreetNumber', 'mailingCity', 'mailingProvince', 'mailingPostalCode', 'mailingCountry'], separator: ', ' },
  { type: 'copy', pdfKey: 'noticeMailingAddress', sourceKey: 'applicantMailingAddress' },

  { type: 'concat', pdfKey: '_consentPerson1OwnAddress', sourceKeys: ['consentMailingAddressLine1', 'consentPerson1City', 'consentPerson1Province', 'consentPerson1Country', 'consentPerson1PostalCode'], separator: ', ' },
  { type: 'copy', pdfKey: 'consentMailingAddressLine1', sourceKey: 'applicantMailingAddress' },

  { type: 'copy', pdfKey: 'consentAppFirstName', sourceKey: 'currentFirstName' },
  { type: 'copy', pdfKey: 'consentAppMiddleName', sourceKey: 'currentMiddleName' },
  { type: 'nameConcat', pdfKey: 'consentAppLastOrSingleName', sourceKeys: ['currentLastOrSingleName'], singleNameFlag: 'hasOnlySingleName', singleNameKey: 'currentSingleName' },

  { type: 'nameConcat', pdfKey: 'consentConsentingPersonName', sourceKeys: ['consentPerson1FirstName', 'consentPerson1MiddleName', 'consentPerson1LastName'], singleNameFlag: 'consentPerson1SingleName', singleNameKey: 'consentPerson1SingleNameText' },
  { type: 'conditionalCopy', pdfKey: 'consentConsentingPersonAddress', flagKey: 'consentPerson1SameAddress', trueSourceKey: 'applicantMailingAddress', falseSourceKey: '_consentPerson1OwnAddress' },
  { type: 'today', pdfKey: 'consentSignedDt' },

  { type: 'nameConcat', pdfKey: 'consentPerson2Name', sourceKeys: ['consentPerson2FirstName', 'consentPerson2MiddleName', 'consentPerson2LastName'], singleNameFlag: 'consentPerson2SingleName', singleNameKey: 'consentPerson2SingleNameText' },
  { type: 'concat', pdfKey: '_consentPerson2OwnAddress', sourceKeys: ['consentPerson2AddressLine1', 'consentPerson2City', 'consentPerson2Province', 'consentPerson2Country', 'consentPerson2PostalCode'], separator: ', ' },
  { type: 'conditionalCopy', pdfKey: 'consentPerson2AddressLine1', flagKey: 'consentPerson2SameAddress', trueSourceKey: 'applicantMailingAddress', falseSourceKey: '_consentPerson2OwnAddress' },
  { type: 'copy', pdfKey: 'consentPerson2Address', sourceKey: 'consentPerson2AddressLine1' },

  { type: 'nameConcat', pdfKey: 'consentPerson3Name', sourceKeys: ['consentPerson3FirstName', 'consentPerson3MiddleName', 'consentPerson3LastName'], singleNameFlag: 'consentPerson3SingleName', singleNameKey: 'consentPerson3SingleNameText' },
  { type: 'concat', pdfKey: '_consentPerson3OwnAddress', sourceKeys: ['consentPerson3AddressLine1', 'consentPerson3City', 'consentPerson3Province', 'consentPerson3Country', 'consentPerson3PostalCode'], separator: ', ' },
  { type: 'conditionalCopy', pdfKey: 'consentPerson3AddressLine1', flagKey: 'consentPerson3SameAddress', trueSourceKey: 'applicantMailingAddress', falseSourceKey: '_consentPerson3OwnAddress' },
  { type: 'copy', pdfKey: 'consentPerson3Address', sourceKey: 'consentPerson3AddressLine1' },

  { type: 'nameConcat', pdfKey: 'consentPerson4Name', sourceKeys: ['consentPerson4FirstName', 'consentPerson4MiddleName', 'consentPerson4LastName'], singleNameFlag: 'consentPerson4SingleName', singleNameKey: 'consentPerson4SingleNameText' },
  { type: 'concat', pdfKey: '_consentPerson4OwnAddress', sourceKeys: ['consentPerson4AddressLine1', 'consentPerson4City', 'consentPerson4Province', 'consentPerson4Country', 'consentPerson4PostalCode'], separator: ', ' },
  { type: 'conditionalCopy', pdfKey: 'consentPerson4AddressLine1', flagKey: 'consentPerson4SameAddress', trueSourceKey: 'applicantMailingAddress', falseSourceKey: '_consentPerson4OwnAddress' },
  { type: 'copy', pdfKey: 'consentPerson4Address', sourceKey: 'consentPerson4AddressLine1' },

  { type: 'nameConcat', pdfKey: 'consentPerson5Name', sourceKeys: ['consentPerson5FirstName', 'consentPerson5MiddleName', 'consentPerson5LastName'], singleNameFlag: 'consentPerson5SingleName', singleNameKey: 'consentPerson5SingleNameText' },
  { type: 'concat', pdfKey: '_consentPerson5OwnAddress', sourceKeys: ['consentPerson5AddressLine1', 'consentPerson5City', 'consentPerson5Province', 'consentPerson5Country', 'consentPerson5PostalCode'], separator: ', ' },
  { type: 'conditionalCopy', pdfKey: 'consentPerson5AddressLine1', flagKey: 'consentPerson5SameAddress', trueSourceKey: 'applicantMailingAddress', falseSourceKey: '_consentPerson5OwnAddress' },
  { type: 'copy', pdfKey: 'consentPerson5Address', sourceKey: 'consentPerson5AddressLine1' },

  { type: 'nameConcat', pdfKey: 'noticeAppName', sourceKeys: ['_noticeAckFirstName', '_noticeAckMiddleName', '_noticeAckLastName'], singleNameFlag: 'noticeSpouseSingleName', singleNameKey: 'noticeSpouseSingleNameText' },
  { type: 'nameConcat', pdfKey: 'noticeAckName', sourceKeys: ['_noticeAckFirstName', '_noticeAckMiddleName', '_noticeAckLastName'], singleNameFlag: 'noticeSpouseSingleName', singleNameKey: 'noticeSpouseSingleNameText' },
  { type: 'conditionalCopy', pdfKey: 'noticeAckAddress', flagKey: 'noticeSpouseSameAddress', trueSourceKey: 'applicantMailingAddress', falseSourceKey: '_noticeSpouseFullAddress' },
  { type: 'today', pdfKey: 'noticeAppSignedDt' },
  { type: 'today', pdfKey: 'noticeSignedDt' },

  { type: 'copy', pdfKey: 'guarantorAppFirstName', sourceKey: 'currentFirstName' },
  { type: 'copy', pdfKey: 'guarantorAppMiddleName', sourceKey: 'currentMiddleName' },
  { type: 'nameConcat', pdfKey: 'guarantorAppLastOrSingleName', sourceKeys: ['currentLastOrSingleName'], singleNameFlag: 'hasOnlySingleName', singleNameKey: 'currentSingleName' },

  { type: 'copy', pdfKey: 'noticeSpouseFirstName', sourceKey: 'currentFirstName' },
  { type: 'copy', pdfKey: 'noticeSpouseMiddleName', sourceKey: 'currentMiddleName' },
  { type: 'nameConcat', pdfKey: 'noticeSpouseLastName', sourceKeys: ['currentLastOrSingleName'], singleNameFlag: 'hasOnlySingleName', singleNameKey: 'currentSingleName' },

  { type: 'copy', pdfKey: 'noticeCurrentFristName', sourceKey: 'currentFirstName' },
  { type: 'copy', pdfKey: 'noticeCurrentMiddleName', sourceKey: 'currentMiddleName' },
  { type: 'nameConcat', pdfKey: 'noticeCurrentLastOrSingleName', sourceKeys: ['currentLastOrSingleName'], singleNameFlag: 'hasOnlySingleName', singleNameKey: 'currentSingleName' },

  { type: 'copy', pdfKey: 'noticeNewFirstName', sourceKey: 'proposedFirstName' },
  { type: 'copy', pdfKey: 'noticeNewMiddleName', sourceKey: 'proposedMiddleName' },
  { type: 'nameConcat', pdfKey: 'noticeNewLastOrSingleName', sourceKeys: ['proposedLastOrSingleName'], singleNameFlag: 'hasProposedSingleName', singleNameKey: 'proposedSingleName' },

  { type: 'concat', pdfKey: 'statutoryAppName', sourceKeys: ['currentFirstName', 'currentMiddleName', 'currentLastOrSingleName'] },
  { type: 'null', pdfKey: 'statutoryAppInitials' },
  { type: 'null', pdfKey: 'statutoryYear' },
  { type: 'null', pdfKey: 'statutoryMonth' },
  { type: 'null', pdfKey: 'statutoryDay' },

  { type: 'copy', pdfKey: 'consentMailingAddress', sourceKey: 'applicantMailingAddress' },
  { type: 'copy', pdfKey: 'traditionalName', sourceKey: 'traditionalCultueName' },
  { type: 'copy', pdfKey: 'chkPersonTradCulMember', sourceKey: 'isTraditionalCulturalMember' },
  { type: 'concat', pdfKey: 'dob', sourceKeys: ['dobYear', 'dobMonth', 'dobDay'], separator: '/' },
  { type: 'concat', pdfKey: 'birthPlace', sourceKeys: ['bornCity', 'bornProvince', 'bornCountry'], separator: ', ' },
  { type: 'null', pdfKey: 'statutoryMunicipality' },
  { type: 'null', pdfKey: 'statutoryCounty' },
  { type: 'null', pdfKey: 'ccAppFirstName' },
  { type: 'null', pdfKey: 'ccAppMiddleName' },
  { type: 'null', pdfKey: 'ccAppLastOrSingleName' },
  { type: 'null', pdfKey: 'ccSubFirstName' },
  { type: 'null', pdfKey: 'ccSubMiddleNAme' },
  { type: 'null', pdfKey: 'ccSubLastOrSingleName' },
];

const buildFieldMap = (programContainer: any): Map<string, any> => {
  const map = new Map<string, any>();
  for (const section of programContainer.ListOfFormSections || []) {
    if (section.Visible === false) continue;
    for (const sub of section.ListOfFormSubSections || []) {
      for (const ele of sub.ListOfFormSectionElements || []) {
        if (ele.LoadBindName) map.set(ele.LoadBindName, ele);
      }
    }
  }
  return map;
};

const getLabelFromField = (field: any, code: string, fieldMap?: Map<string, any> | null): string | null => {
  if (!code) return null;
  if (field?.ListOfLOVValues) {
    const option = field.ListOfLOVValues.find((lov: any) => lov.code === code);
    if (option?.description) return option.description;
  }
  if (fieldMap) {
    const countryLookup = fieldMap.get('countryLookup');
    if (countryLookup?.ListOfLOVValues) {
      const option = countryLookup.ListOfLOVValues.find((lov: any) => lov.code === code);
      if (option?.description) return option.description;
    }
  }
  return null;
};

const applyDerivedFields = (payload: Record<string, any>, now: Date) => {
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const yyyy = String(now.getFullYear());
  const todayFormatted = `${yyyy}/${mm}/${dd}`;

  for (const d of DERIVED_PDF_FIELDS) {
    switch (d.type) {
      case 'concat': {
        const parts = d.sourceKeys.map(k => payload[k]).map(v => v != null ? String(v).trim() : '').filter(Boolean);
        payload[d.pdfKey] = parts.join(d.separator || ' ');
        break;
      }
      case 'copy':
        payload[d.pdfKey] = payload[d.sourceKey] || '';
        break;
      case 'today':
        payload[d.pdfKey] = todayFormatted;
        break;
      case 'todaySplit':
        payload[d.pdfKeys[0]] = yyyy;
        payload[d.pdfKeys[1]] = mm;
        payload[d.pdfKeys[2]] = dd;
        break;
      case 'null':
        payload[d.pdfKey] = null;
        break;
      case 'initials': {
        const chars = d.sourceKeys
          .map(k => payload[k])
          .filter(Boolean)
          .map((v: string) => v.charAt(0).toUpperCase());
        payload[d.pdfKey] = chars.join('');
        break;
      }
      case 'conditionalCopy': {
        const flag = payload[d.flagKey] === true || payload[d.flagKey] === 'true';
        payload[d.pdfKey] = flag ? (payload[d.trueSourceKey] || '') : (payload[d.falseSourceKey] || '');
        break;
      }
      case 'nameConcat': {
        const isSingle = payload[d.singleNameFlag] === true || payload[d.singleNameFlag] === 'true';
        if (isSingle) {
          payload[d.pdfKey] = payload[d.singleNameKey] || '';
        } else {
          const nameParts = d.sourceKeys.map(k => payload[k]).map(v => v != null ? String(v).trim() : '').filter(Boolean);
          payload[d.pdfKey] = nameParts.join(' ');
        }
        break;
      }
    }
  }
};

type ChecklistState = 'Must' | 'DoNot' | 'May';

const setChecklistItem = (payload: Record<string, any>, num: number, state: ChecklistState) => {
  payload[`chk${num}Must`] = state === 'Must' ? 'true' : '';
  payload[`chk${num}DoNot`] = state === 'DoNot' ? 'true' : '';
  payload[`chk${num}May`] = state === 'May' ? 'true' : '';
};

const applyChecklistFields = (payload: Record<string, any>, uiData: Record<string, any>, now: Date) => {
  const isMarried = uiData.isMarried === 'true';
  const isConjugal = uiData.isConjugalRelationshipValid === 'true';
  const isMarriedOrConjugal = isMarried || isConjugal;

  let isMinor = false;
  const dobYear = Number(payload.dobYear);
  const dobMonth = Number(payload.dobMonth);
  const dobDay = Number(payload.dobDay);
  if (dobYear && dobMonth && dobDay) {
    const birthDate = new Date(dobYear, dobMonth - 1, dobDay);
    let age = now.getFullYear() - birthDate.getFullYear();
    const m = now.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < birthDate.getDate())) age--;
    isMinor = age === 16 || age === 17;
  }

  const hasCriminalHistory =
    uiData.hasBeenConvicted === 'true' ||
    uiData.hasBeenFoundGuiltyNoDischarge === 'true' ||
    uiData.hasOutstandingLawEnforcementOrders === 'true' ||
    uiData.hasPendingCriminalCharges === 'true';

  setChecklistItem(payload, 1, isMarriedOrConjugal ? 'Must' : 'DoNot');
  setChecklistItem(payload, 2, isMinor && !isMarried ? 'Must' : 'DoNot');
  setChecklistItem(payload, 3, (uiData.hasProposedSingleName === 'true' || uiData.hasProposedSingleName === true) ? 'Must' : 'DoNot');
  setChecklistItem(payload, 4, hasCriminalHistory ? 'Must' : 'DoNot');
  setChecklistItem(payload, 5, 'Must');
  setChecklistItem(payload, 6, 'Must');
  setChecklistItem(payload, 7, uiData.hasLegallyChangedNameBefore === 'true' ? 'Must' : 'DoNot');
  setChecklistItem(payload, 8, isMinor && !isMarried ? 'May' : 'DoNot');
  setChecklistItem(payload, 9, isMarried ? 'Must' : 'DoNot');
  setChecklistItem(payload, 10, 'May');
  setChecklistItem(payload, 11, 'Must');
  setChecklistItem(payload, 12, 'Must');
  setChecklistItem(payload, 13, 'May' /*uiData.isIndigenous === 'true' ? 'May' : 'DoNot'*/);
  setChecklistItem(payload, 14, 'Must');

  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  payload.chklistDate = `${now.getFullYear()}/${mm}/${dd}`;
};

const applyLivedInOntarioFromDob = (payload: Record<string, any>, uiData: Record<string, any>, now: Date) => {
  if (uiData.hasLivedInOntarioConfirmation !== true) return;

  const dobYear = Number(payload.dobYear);
  const dobMonth = Number(payload.dobMonth);
  const dobDay = Number(payload.dobDay);
  if (!dobYear || !dobMonth || !dobDay) return;

  const birthDate = new Date(dobYear, dobMonth - 1, dobDay);
  let years = now.getFullYear() - birthDate.getFullYear();
  let months = now.getMonth() - birthDate.getMonth();
  if (now.getDate() < birthDate.getDate()) {
    months--;
  }
  if (months < 0) {
    years--;
    months += 12;
  }
  payload.livedYears = String(years);
  payload.livedMonths = String(months);
};

export const transformToPdfPayload = (
  uiData: Record<string, any>,
  programContainer?: any
): Record<string, any> => {
  const payload: Record<string, any> = {};
  const fieldMap = programContainer ? buildFieldMap(programContainer) : null;
  const now = new Date();

  const applyMapping = (m: FieldMapping, uiKey: string, value: any) => {
    switch (m.type) {
      case 'direct':
        payload[m.pdfKey] = typeof value === 'boolean' ? String(value) : value;
        break;

      case 'lookup': {
        if (fieldMap && value) {
          const field = fieldMap.get(uiKey) || null;
          const label = getLabelFromField(field, value, fieldMap);
          payload[m.pdfKey] = label || value;
        } else {
          payload[m.pdfKey] = value;
        }
        break;
      }

      case 'lookupWithFallback': {
        const otherValue = uiData[m.otherUiKey];
        if (otherValue) {
          payload[m.pdfKey] = otherValue;
        } else if (fieldMap && value) {
          const field = fieldMap.get(uiKey) || null;
          const label = getLabelFromField(field, value, fieldMap);
          payload[m.pdfKey] = label || value;
        } else {
          payload[m.pdfKey] = value;
        }
        break;
      }

      case 'dateSplit': {
        if (value && typeof value === 'string' && value.includes('/')) {
          const [month, day, year] = value.split('/');
          payload[m.pdfKeys[0]] = year;
          payload[m.pdfKeys[1]] = month;
          payload[m.pdfKeys[2]] = day;
        }
        break;
      }

      case 'dateReformat': {
        if (value && typeof value === 'string' && value.includes('/')) {
          const [month, day, year] = value.split('/');
          payload[m.pdfKey] = `${year}/${month.padStart(2, '0')}/${day.padStart(2, '0')}`;
        } else {
          payload[m.pdfKey] = value;
        }
        break;
      }

      case 'address': {
        const parts = [value, uiData[m.line2UiKey]].filter(Boolean);
        payload[m.pdfKey] = parts.join(', ');
        break;
      }

      case 'nameOrSingle':
        payload[m.pdfKey] = uiData[m.flagUiKey]
          ? (uiData[m.textUiKey] || value)
          : value;
        break;

      case 'yesNo': {
        const isTrue = value === true || value === 'true';
        payload[m.yesKey] = isTrue ? 'true' : '';
        payload[m.noKey] = isTrue ? '' : 'true';
        break;
      }

      case 'negate':
        payload[m.pdfKey] = (value === true || value === 'true') ? 'false' : 'true';
        break;
    }
  };

  for (const [uiKey, value] of Object.entries(uiData)) {
    const entry = VECON_FIELD_MAPPINGS[uiKey];
    if (!entry) continue;

    const mappings = Array.isArray(entry) ? entry : [entry];
    for (const m of mappings) {
      if (!m) continue;
      applyMapping(m, uiKey, value);
    }
  }

  applyLivedInOntarioFromDob(payload, uiData, now);

  applyDerivedFields(payload, now);
  applyChecklistFields(payload, uiData, now);

  return payload;
};
